/**
 * Client-side structural pre-check for a self-chosen PIN.
 *
 * A deliberate, partial copy of `isWeakPin` in `functions/src/security/pin.js`:
 * the same five structural rules (all-identical, repeated blocks, doubled
 * pairs, ascending/descending runs), but never the curated blocklist. The
 * server's blocklist stays server-only and is not mirrored here — that is
 * intentional, not an oversight, so do not "complete" the parity by copying it
 * across.
 *
 * Used only to give instant feedback in the Replace credentials dialog before
 * a round trip, mirroring the same dialog's existing client-side echo of the
 * server's "choose at least one credential" refusal. The server's own check
 * (`isWeakPin`) is the one that actually enforces this — this module must
 * never be treated as authoritative.
 *
 * Pure and DOM-free so it can be unit-tested in the node environment the root
 * Vitest config uses.
 */

/**
 * Whether a 6-digit PIN has an obvious structural pattern.
 *
 * Kept in sync with the first five rules of `isWeakPin` in
 * `functions/src/security/pin.js`. Change one, consider the other.
 *
 * @param {string} pin A 6-digit string.
 * @returns {boolean}
 */
export function hasObviousPinPattern(pin) {
  if (!/^\d{6}$/.test(pin)) {
    return true;
  }
  // All six digits identical: 111111.
  if (/^(\d)\1{5}$/.test(pin)) {
    return true;
  }
  // A 2-digit block repeated three times: 121212, 101010.
  if (/^(\d{2})\1{2}$/.test(pin)) {
    return true;
  }
  // A 3-digit block repeated twice: 123123.
  if (/^(\d{3})\1$/.test(pin)) {
    return true;
  }
  // Three doubled pairs: 112233, 998877.
  if (/^(\d)\1(\d)\2(\d)\3$/.test(pin)) {
    return true;
  }
  // An ascending or descending run of consecutive digits, wrapping at the ends
  // of the digit circle: 123456, 567890, 987654.
  const digits = pin.split("").map(Number);
  let ascending = true;
  let descending = true;
  for (let index = 1; index < digits.length; index += 1) {
    const current = digits[index];
    const previous = digits[index - 1];
    // Unreachable given the 6-digit regex guard above; satisfies the type
    // checker's noUncheckedIndexedAccess rather than asserting past it.
    if (current === undefined || previous === undefined) {
      continue;
    }
    if (current !== (previous + 1) % 10) {
      ascending = false;
    }
    if (current !== (previous + 9) % 10) {
      descending = false;
    }
  }
  return ascending || descending;
}
